import { useEffect, useState } from 'react';
import { Plus, ArrowLeft, Pencil } from 'lucide-react';
import Layout from '../components/Layout';
import BankAccountModal from '../components/BankAccountModal';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency } from '../lib/api';

const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editBank, setEditBank] = useState(null);
  const { isViewer } = useAuth();

  const fetchBanks = () => {
    api.get('/bank-accounts').then(res => setBanks(res.data));
  };

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
    fetchBanks();
  }, []);

  const openBank = async (bank) => {
    setSelectedBank(bank);
    const res = await api.get('/payments');
    setTransactions(res.data.filter(p => p.bank_account_id === bank.id));
  };

  const totalBalance = banks.reduce((s, b) => s + parseFloat(b.balance), 0);
  const companyName = (id) => companies.find(c => c.id === id)?.full_name || '—';

  return (
    <Layout
      title="Banks"
      action={
        !selectedBank && !isViewer && (
          <button data-testid="new-bank-button" onClick={() => { setEditBank(null); setShowModal(true); }} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-lg flex items-center transition-colors">
            <Plus className="w-5 h-5 mr-2" strokeWidth={1} /> New Account
          </button>
        )
      }
    >
      {!selectedBank ? (
        <>
          <div data-testid="total-balance" className="stat-card bg-surface border border-border p-6 mb-8 fade-up">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Aggregate Balance · {banks.length} accounts</p>
            <p className="font-numbers text-4xl font-light text-text-primary tabular-nums">{formatCurrency(totalBalance)}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banks.map((b, i) => (
              <div
                key={b.id}
                data-testid={`bank-card-${b.label.replace(/\s/g, '-')}`}
                className={`stat-card bg-surface border border-border p-6 cursor-pointer fade-up fade-up-d${Math.min(i, 3)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">{companyName(b.company_id)}</p>
                  {!isViewer && (
                    <button
                      data-testid={`edit-bank-${b.id}`}
                      onClick={(e) => { e.stopPropagation(); setEditBank(b); setShowModal(true); }}
                      className="text-text-secondary hover:text-maroon transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1} />
                    </button>
                  )}
                </div>
                <div onClick={() => openBank(b)}>
                  <h3 className="font-heading text-xl font-light text-text-primary mb-4">{b.label}</h3>
                  <div className="space-y-1 mb-4 pb-4 border-b border-border">
                    <p className="font-body text-sm text-text-secondary">{b.bank_name || '—'}</p>
                    <p className="font-body text-xs text-text-secondary mono-num">
                      ····{b.account_number_last4 || '----'} · {b.account_type || '—'}
                    </p>
                  </div>
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Balance</p>
                  <p className="font-numbers text-2xl text-text-primary tabular-nums">{formatCurrency(b.balance)}</p>
                </div>
              </div>
            ))}
            {banks.length === 0 && (
              <div className="col-span-3 text-center py-12 font-body text-sm text-text-secondary border border-border border-dashed">
                No bank accounts yet · Click &ldquo;New Account&rdquo; to add
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="fade-up">
          <button data-testid="back-to-banks" onClick={() => setSelectedBank(null)} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover mb-4 flex items-center transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1} /> Back
          </button>
          <div className="bg-surface border border-border p-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">{companyName(selectedBank.company_id)}</p>
                <h2 className="font-heading text-2xl font-light text-text-primary mb-1">{selectedBank.label}</h2>
                <p className="font-body text-sm text-text-secondary mono-num">{selectedBank.bank_name} · ····{selectedBank.account_number_last4}</p>
              </div>
              {!isViewer && (
                <button
                  data-testid="edit-current-bank"
                  onClick={() => { setEditBank(selectedBank); setShowModal(true); }}
                  className="text-text-secondary hover:text-maroon transition-colors flex items-center font-body text-xs uppercase tracking-widest"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" strokeWidth={1} /> Edit
                </button>
              )}
            </div>
            <p className="font-numbers text-3xl text-text-primary tabular-nums">{formatCurrency(selectedBank.balance)}</p>
          </div>

          <div className="bg-surface border border-border p-6">
            <h3 className="font-heading text-xl font-light mb-4">Transactions · {transactions.length}</h3>
            <table className="w-full">
              <thead>
                <tr>
                  <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
                  <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice</th>
                  <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
                  <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Mode</th>
                  <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-b border-border hover:bg-cream transition-colors">
                    <td className="py-3 font-body text-sm mono-num">{t.date}</td>
                    <td className="py-3 font-body text-sm">{t.invoice_no}</td>
                    <td className="py-3 font-body text-sm">{t.client_name}</td>
                    <td className="py-3 font-body text-sm">{t.mode}</td>
                    <td className="py-3 font-numbers text-sm text-success text-right tabular-nums">+ {formatCurrency(t.amount)}</td>
                  </tr>
                ))}
                {transactions.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-text-secondary font-body text-sm">No transactions</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <BankAccountModal
          existing={editBank}
          onClose={() => { setShowModal(false); setEditBank(null); }}
          onSaved={() => { setShowModal(false); setEditBank(null); fetchBanks(); if (selectedBank) openBank(selectedBank); }}
        />
      )}
    </Layout>
  );
};

export default Banks;
